import { useForm } from '@refinedev/react-hook-form';
import type { HttpError } from '@refinedev/core';
import { useNavigate } from 'react-router';
import type { TfTrackRow, TrackManifest } from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// HUB-062 / DEV-014: edits go through the manifest-upsert path in the data
// provider. The form only edits the operator-visible fields; the existing
// openapi + systemPrompt are preserved unless the form overrides them.
interface FormValues {
  name: string;
  displayName: string;
  description: string;
  apiUrl: string;
  baseUrl: string;
  healthCheckUrl: string;
}

const fromTrack = (t: Partial<TfTrackRow>): FormValues => ({
  name: t.name ?? '',
  displayName: t.displayName ?? '',
  description: t.description ?? '',
  apiUrl: t.apiUrl ?? '',
  baseUrl: t.baseUrl ?? '',
  healthCheckUrl: t.healthCheckUrl ?? '',
});

const toBody = (v: FormValues): Partial<TrackManifest> => {
  const body: Partial<TrackManifest> = {};
  if (v.name.trim()) body.name = v.name.trim();
  if (v.displayName.trim()) body.displayName = v.displayName.trim();
  if (v.description.trim()) body.description = v.description.trim();
  if (v.apiUrl.trim()) body.apiUrl = v.apiUrl.trim();
  if (v.baseUrl.trim()) body.baseUrl = v.baseUrl.trim();
  if (v.healthCheckUrl.trim()) body.healthCheckUrl = v.healthCheckUrl.trim();
  return body;
};

export function TracksEdit() {
  const navigate = useNavigate();
  const {
    refineCore: { onFinish, query },
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<TfTrackRow, HttpError, FormValues>({
    refineCoreProps: { onMutationSuccess: () => navigate('/admin/tracks') },
  });

  const initial = query?.data?.data
    ? fromTrack(query.data.data)
    : { name: '', displayName: '', description: '', apiUrl: '', baseUrl: '', healthCheckUrl: '' };

  const submit = handleSubmit(async (values) => {
    await onFinish(toBody(values) as unknown as FormValues);
  });

  if (query?.isLoading) return <div>Loading…</div>;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Edit track</h1>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Track manifest</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-sm text-muted-foreground">
            OpenAPI + system prompt are managed through the track-side <code className="font-mono">registerTrack()</code> flow.
          </p>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="name">Name (slug)</Label>
              <Input id="name" defaultValue={initial.name} {...register('name')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="displayName">Display name</Label>
              <Input id="displayName" defaultValue={initial.displayName} {...register('displayName')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input id="description" defaultValue={initial.description} {...register('description')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="apiUrl">API URL</Label>
              <Input id="apiUrl" type="url" defaultValue={initial.apiUrl} {...register('apiUrl')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="baseUrl">Frontend URL</Label>
              <Input id="baseUrl" type="url" defaultValue={initial.baseUrl} {...register('baseUrl')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="healthCheckUrl">Health-check URL</Label>
              <Input id="healthCheckUrl" type="url" defaultValue={initial.healthCheckUrl} {...register('healthCheckUrl')} />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving…' : 'Save'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/admin/tracks')}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
